/**
 * iPad: technician and customer signatures on each form the job needs (e.g. Shop Report and
 * Rental Check-In). The certification statement is shown above the signature line for the
 * Field / Shop Report and Lube Report. The PNG is queued like a photo, then the signature item
 * that points at it — so signing works with no signal. If the customer isn't there, the
 * technician records "Customer not present" with a reason (Bruce, 2026-11-04).
 */
import { useRef, useState } from 'react';
import { SIGNATURE_FORM_LABELS, type SignatureForm, type SignatureRole } from '@wo/shared';
import { SignatureBox, type SignatureHandle } from '../components/SignatureBox';
import { enqueueFile, enqueueItem } from './queue';
import type { FormLists, JobsPayload, MechForms, MechJob, MechSignatureSet } from './types';
import { Big, BigField, NoteBox, Sheet } from './ui';

type SetForms = (fn: (f: MechForms) => MechForms) => void;

const NOT_PRESENT_MIN = 10;
const QUICK_REASONS = ['Customer not on site', 'Machine dropped off, customer not here'];

export function SignSheet({ job, payload, lists, userId, setForms, onClose }: { job: MechJob; payload: JobsPayload; lists: FormLists; userId: number; setForms: SetForms; onClose: () => void }) {
  const [pad, setPad] = useState<{ form: SignatureForm; role: SignatureRole } | null>(null);
  const [notPresent, setNotPresent] = useState(false);
  const f = job.forms;
  const sigs = f.signatures;
  const sets: MechSignatureSet[] = f.signatureSets ?? [
    { form: f.signatureForm, label: SIGNATURE_FORM_LABELS[f.signatureForm], certified: f.signatureForm === 'FIELD_REPORT' || f.signatureForm === 'LUBE', technician: sigs.technician, customer: sigs.customer },
  ];
  const canSign = job.status === 'ASSIGNED' || job.status === 'IN_PROGRESS' || job.status === 'ON_HOLD' || job.status === 'READY_FOR_REVIEW';

  if (pad) {
    const set = sets.find((s) => s.form === pad.form)!;
    return (
      <Pad
        title={`${pad.role === 'TECHNICIAN' ? `Technician signature — ${payload.me.name}` : 'Customer signature'} (${set.label})`}
        statement={set.certified ? lists.certification.text : null}
        needName={pad.role === 'CUSTOMER'}
        onCancel={() => setPad(null)}
        onSave={async (blob, signerName) => {
          const { form, role } = pad;
          const fileClientId = await enqueueFile(userId, `Signature #${job.number}`, blob, { workOrderId: job.id, kind: 'SIGNATURE', purpose: 'signature' });
          await enqueueItem(userId, `Signature #${job.number}`, { type: 'signature', workOrderId: job.id, form, role, fileClientId, ...(signerName ? { signerName } : {}) });
          const entry = { signerName: signerName ?? payload.me.name, signedAt: new Date().toISOString() };
          const key = role === 'TECHNICIAN' ? 'technician' : 'customer';
          setForms((x) => ({
            ...x,
            signatures: form === x.signatureForm ? { ...x.signatures, [key]: entry } : x.signatures,
            signatureSets: (x.signatureSets ?? sets).map((s) => (s.form === form ? { ...s, [key]: entry } : s)),
          }));
          setPad(null);
        }}
      />
    );
  }

  if (notPresent) {
    return (
      <NotPresentSheet
        current={sigs.customerNotPresentReason ?? null}
        onClose={() => setNotPresent(false)}
        onSave={async (reason) => {
          await enqueueItem(userId, `Customer not present #${job.number}`, { type: 'customer_not_present', workOrderId: job.id, reason });
          setForms((x) => ({ ...x, signatures: { ...x.signatures, customerNotPresent: reason !== null, customerNotPresentReason: reason } }));
          setNotPresent(false);
        }}
      />
    );
  }

  return (
    <Sheet title="Signatures" onClose={onClose}>
      <div className="grid gap-3">
        {sets.map((s) => (
          <section key={s.form} className="grid gap-3 rounded-2xl border-2 border-slate-200 p-3">
            <h3 className="text-xl font-bold">{s.label}</h3>
            <SigRow label="Technician" done={s.technician} />
            <Big tone={s.technician ? 'light' : 'go'} className={s.technician ? 'border-2 border-slate-300' : ''} disabled={!canSign} onClick={() => setPad({ form: s.form, role: 'TECHNICIAN' })}>
              {s.technician ? 'Sign again (technician)' : 'Technician signs'}
            </Big>
            <SigRow label="Customer" done={s.customer} notPresent={sigs.customerNotPresent} />
            <Big tone={s.customer ? 'light' : 'go'} className={s.customer ? 'border-2 border-slate-300' : ''} disabled={!canSign} onClick={() => setPad({ form: s.form, role: 'CUSTOMER' })}>
              {s.customer ? 'Customer signs again' : 'Hand the iPad to the customer'}
            </Big>
          </section>
        ))}
        <Big tone={sigs.customerNotPresent ? 'light' : 'warn'} className={sigs.customerNotPresent ? 'border-2 border-slate-300' : ''} disabled={!canSign} onClick={() => setNotPresent(true)}>
          {sigs.customerNotPresent ? 'Change "Customer not present"' : 'Customer not present'}
        </Big>
        {sigs.customerNotPresentReason && <p className="text-base text-slate-600">Customer not present: “{sigs.customerNotPresentReason}”</p>}
      </div>
    </Sheet>
  );
}

function NotPresentSheet({ current, onClose, onSave }: { current: string | null; onClose: () => void; onSave: (reason: string | null) => void }) {
  const [reason, setReason] = useState(current ?? '');
  return (
    <Sheet title="Customer not present" onClose={onClose}>
      <p className="mb-2 text-lg">The job can close without the customer's signature. Say why they couldn't sign — the Manager sees it.</p>
      <div className="mb-3 grid gap-2">
        {QUICK_REASONS.map((r) => (
          <Big key={r} tone={reason === r ? 'go' : 'light'} className="border-2 border-slate-300" onClick={() => setReason(r)}>{r}</Big>
        ))}
      </div>
      <NoteBox className="min-h-[100px]" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Or type the reason" />
      <div className="mt-3 grid grid-cols-2 gap-3">
        {current ? <Big tone="light" className="border-2 border-slate-300" onClick={() => onSave(null)}>Customer did sign — clear it</Big> : <span />}
        <Big tone="go" disabled={reason.trim().length < NOT_PRESENT_MIN} onClick={() => onSave(reason.trim())}>Save</Big>
      </div>
      {reason.trim().length > 0 && reason.trim().length < NOT_PRESENT_MIN && <p className="mt-2 text-base text-slate-600">At least {NOT_PRESENT_MIN} characters.</p>}
    </Sheet>
  );
}

function SigRow({ label, done, notPresent }: { label: string; done: { signerName: string; signedAt: string } | null; notPresent?: boolean }) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-slate-100 p-3 text-lg">
      <span className={`text-2xl font-black ${done || notPresent ? 'text-green-700' : 'text-red-700'}`}>{done || notPresent ? '✓' : '✗'}</span>
      <span className="flex-1">
        <b>{label}</b>
        {done ? ` — ${done.signerName}, ${new Date(done.signedAt).toLocaleString()}` : notPresent ? ' — "Customer not present" recorded' : ' — not signed'}
      </span>
    </div>
  );
}

function Pad({ title, statement, needName, onCancel, onSave }: { title: string; statement: string | null; needName: boolean; onCancel: () => void; onSave: (png: Blob, name: string | null) => void }) {
  const pad = useRef<SignatureHandle>(null);
  const [empty, setEmpty] = useState(true);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white p-4">
      <h2 className="text-2xl font-bold">{title}</h2>
      {statement && <p className="my-2 max-h-40 overflow-y-auto text-base leading-snug text-slate-700">{statement}</p>}
      <SignatureBox ref={pad} height={300} onChange={setEmpty} className="my-2" />
      <p className="text-base text-slate-600">Sign in the box with a finger.</p>
      {needName && <BigField label="Printed name" value={name} onChange={(e) => setName(e.target.value)} autoCapitalize="words" />}
      <div className="mt-auto grid grid-cols-3 gap-3 pt-4">
        <Big tone="light" className="border-2 border-slate-300" onClick={onCancel}>Cancel</Big>
        <Big tone="light" className="border-2 border-slate-300" onClick={() => pad.current?.clear()}>Clear</Big>
        <Big tone="go" disabled={empty || busy || (needName && name.trim().length < 2)} onClick={async () => {
          setBusy(true);
          try {
            onSave(await pad.current!.toBlob(), needName ? name.trim() : null);
          } finally {
            setBusy(false);
          }
        }}>Done</Big>
      </div>
    </div>
  );
}
